import { useState, type FormEvent } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

export function SignupPage(): JSX.Element {
  const { user, signup } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) {
      setErr('Les mots de passe ne correspondent pas.');
      return;
    }
    if (password.length < 12) {
      setErr('Le mot de passe doit faire au moins 12 caractères.');
      return;
    }
    setLoading(true);
    try {
      await signup(email, username, password);
      navigate('/');
    } catch (e: unknown) {
      setErr((e as Error).message ?? 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
      <form
        onSubmit={submit}
        style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '32px',
          width: '420px',
        }}
      >
        <h1 style={{ marginTop: 0, fontSize: '22px' }}>🔮 Créer un compte Siverix</h1>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '13px' }}>
          Le premier compte créé devient automatiquement administrateur de l'instance.
        </p>

        <label style={labelStyle}>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          Pseudo (3-32 car., a-z 0-9 _ -)
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            minLength={3}
            maxLength={32}
            pattern="^[a-z0-9_-]+$"
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          Mot de passe (12 car. min)
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={12}
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          Confirmation
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={12}
            style={inputStyle}
          />
        </label>

        {err && (
          <div
            role="alert"
            style={{
              marginTop: '12px',
              padding: '8px 12px',
              borderRadius: '6px',
              background: 'rgba(248,81,73,.15)',
              color: 'var(--color-error)',
              fontSize: '13px',
            }}
          >
            {err}
          </div>
        )}

        <button type="submit" disabled={loading} style={primaryButtonStyle}>
          {loading ? '…' : 'Créer le compte'}
        </button>

        <p style={{ marginTop: '16px', fontSize: '13px', color: 'var(--color-text-muted)' }}>
          Déjà inscrit ? <Link to="/login">Se connecter</Link>
        </p>
      </form>
    </div>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: '12px', marginTop: '12px' };

const inputStyle: React.CSSProperties = {
  width: '100%',
  marginTop: '4px',
  padding: '10px 12px',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  border: '1px solid var(--color-border)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'inherit',
};

const primaryButtonStyle: React.CSSProperties = {
  marginTop: '20px',
  width: '100%',
  padding: '12px',
  background: 'var(--color-accent)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
};
